import { toastError, toastSuccess } from "../components/atoms/CustomToast";

/**
 * Fungsi untuk mendownload file dari URL yang diberikan
 * @param {string} fileUrl - URL file yang akan didownload
 * @param {string} fileName - Nama file yang akan disimpan (opsional)
 * @returns {Promise<boolean>} - Returns true jika berhasil, throws error jika gagal
 */
export const downloadFile = async (fileUrl, fileName = '') => {
  try {
    // Validasi URL
    if (!fileUrl) {
      throw new Error('URL file tidak boleh kosong');
    }

    // Buat element link untuk download
    const link = document.createElement('a');
    link.href = fileUrl;
    
    // Jika fileName tidak diisi, ambil nama file dari URL
    if (!fileName) {
      const urlParts = fileUrl.split('/');
      fileName = urlParts[urlParts.length - 1];
    }
    
    // Set attribute download dengan nama file
    link.setAttribute('download', fileName);
    
    // Append link ke body
    document.body.appendChild(link);
    
    // Trigger click untuk memulai download
    link.click();
    
    // Hapus element link setelah selesai
    link.remove();

    toastSuccess('File berhasil didownload');
    return true;
  } catch (error) {
    console.error('Error downloading file:', error);
    let errorMessage = 'Terjadi kesalahan saat mendownload file';
    
    if (error?.response?.data?.errors?.[0]?.message) {
      errorMessage = error.response.data.errors[0].message;
    } else if (error?.response?.status === 401) {
      errorMessage = 'Sesi Anda telah berakhir, silakan login kembali';
    } else if (error?.response?.status === 403) {
      errorMessage = 'Anda tidak memiliki akses untuk mendownload file ini';
    } else if (error?.response?.status === 404) {
      errorMessage = 'File tidak ditemukan';
    } else if (error?.response?.status >= 500) {
      errorMessage = 'Terjadi kesalahan pada server';
    }

    toastError(errorMessage);
    throw new Error(errorMessage);
  }
};

/**
 * Fungsi untuk mendownload file dengan progress
 * @param {string} fileUrl - URL file yang akan didownload
 * @param {string} fileName - Nama file yang akan disimpan (opsional)
 * @param {function} onProgress - Callback untuk progress download (opsional)
 * @returns {Promise<boolean>} - Returns true jika berhasil, throws error jika gagal
 */
export const downloadFileWithProgress = async (fileUrl, fileName = '', onProgress = null) => {
  try {
    // Validasi URL
    if (!fileUrl) {
      throw new Error('URL file tidak boleh kosong');
    }

    // Fetch file dengan XMLHttpRequest untuk mendapatkan progress
    const xhr = new XMLHttpRequest();
    xhr.responseType = 'blob';

    return new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status === 200) {
          // Buat URL object dari response
          const blob = xhr.response;
          const url = window.URL.createObjectURL(blob);
          
          // Buat element link untuk download
          const link = document.createElement('a');
          link.href = url;
          
          // Jika fileName tidak diisi, coba ambil dari Content-Disposition atau URL
          if (!fileName) {
            const disposition = xhr.getResponseHeader('Content-Disposition');
            if (disposition && disposition.includes('filename=')) {
              fileName = disposition.split('filename=')[1].replace(/["']/g, '');
            } else {
              const urlParts = fileUrl.split('/');
              fileName = urlParts[urlParts.length - 1];
            }
          }
          
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          
          // Cleanup
          window.URL.revokeObjectURL(url);
          toastSuccess('File berhasil didownload');
          resolve(true);
        } else {
          let errorMessage = 'Terjadi kesalahan saat mendownload file';
          
          if (xhr.status === 401) {
            errorMessage = 'Sesi Anda telah berakhir, silakan login kembali';
          } else if (xhr.status === 403) {
            errorMessage = 'Anda tidak memiliki akses untuk mendownload file ini';
          } else if (xhr.status === 404) {
            errorMessage = 'File tidak ditemukan';
          } else if (xhr.status >= 500) {
            errorMessage = 'Terjadi kesalahan pada server';
          }

          toastError(errorMessage);
          reject(new Error(errorMessage));
        }
      };

      xhr.onerror = () => {
        const errorMessage = 'Terjadi kesalahan jaringan';
        toastError(errorMessage);
        reject(new Error(errorMessage));
      };

      if (onProgress) {
        xhr.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            onProgress(percentComplete);
          }
        };
      }

      xhr.open('GET', fileUrl);
      xhr.send();
    });
  } catch (error) {
    console.error('Error downloading file:', error);
    let errorMessage = 'Terjadi kesalahan saat mendownload file';
    
    if (error?.response?.data?.errors?.[0]?.message) {
      errorMessage = error.response.data.errors[0].message;
    }
    
    toastError(errorMessage);
    throw new Error(errorMessage);
  }
}; 